import { ApiError, errorKeyForSuiteApiResult } from './errors';
import { PortalClient } from './portal-client';
import type {
  ConfirmEmailRequest,
  ConfirmEmailResult,
  UserProfileSettings,
  UserProfileUpdateRequest,
} from './types';
import { ESuiteApiResult } from './types';

/**
 * User API — thin, typed wrapper over the Portal's anonymous e-mail confirmation endpoint:
 *
 *   PUT SuiteAPI/User/ConfirmEmailV1
 *   Headers: brandID (no userName/userPWD — the activation token in the body authorises the call).
 *   Body (ConfirmEmailRequest): { userID: string; token: string }
 *   Response: SuiteApiResult<ConfirmEmailResult> — `data` carries the session JWT.
 *
 * See Server/Portal/Areas/SuiteAPI/Controllers/UserController.cs for the Portal-side implementation.
 */
export class UserApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Confirms a self-registered user's e-mail and returns the JWT session bootstrap data.
   *
   * @throws {ApiError} with a translatable key on an invalid/expired token or non-success result.
   */
  public async confirmEmail(request: ConfirmEmailRequest, signal?: AbortSignal): Promise<ConfirmEmailResult> {
    const result = await this.client.request<ConfirmEmailResult>({
      method: 'PUT',
      path: '/SuiteAPI/User/ConfirmEmailV1',
      body: request,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Fetches the authenticated user's personal settings.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async getProfile(signal?: AbortSignal): Promise<UserProfileSettings> {
    const result = await this.client.request<UserProfileSettings>({
      method: 'GET',
      path: '/SuiteAPI/User/GetProfile',
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Updates the authenticated user's personal settings.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async updateProfile(
    request: UserProfileUpdateRequest,
    signal?: AbortSignal,
  ): Promise<UserProfileSettings> {
    const result = await this.client.request<UserProfileSettings>({
      method: 'PUT',
      path: '/SuiteAPI/User/UpdateProfile',
      body: request,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }
}
